import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";

/* ================= TYPES ================= */
type ConfirmedPromotion = {
  emp: string;
  name: string;
  oldDes: string;
  oldGrade: string;
  newDes: string;
  newGrade: string;
  date: string;
  order: string;
  sap: "Synced" | "Pending" | "Error";
};

interface TableColumn<T> {
  title: string;
  key: string;
  className?: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
}

/* ================= COMPONENTS ================= */
const DashboardTile = ({
  title,
  icon,
  count,
  link,
  colorClass = "text-primary",
}: {
  title: string;
  icon?: string;
  count?: string | number;
  link: string;
  colorClass?: string;
}) => {
  return (
    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-6 mb-3">
      <div
        className="card h-100 border-0 shadow-sm hover-card"
        style={{
          borderRadius: "16px",
          transition: "all 0.3s ease",
          cursor: "pointer",
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
      >
        <div className="card-body d-flex align-items-center p-3">
          <div
            className={`d-flex align-items-center justify-content-center me-3 rounded-circle`}
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
              border: "1px solid rgba(0,0,0,0.03)"
            }}
          >
            {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
          </div>
          <div>
            <div className="text-muted small mb-1" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>{title}</div>
            <div className={`h4 fw-bold mb-0 ${colorClass}`}>{count}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="mb-4">
      <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </h5>
      <div className="row">{children}</div>
    </div>
  );
};

/* ================= MAIN ================= */
const ProbationConfirmation: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramCycleId = queryParams.get("cycleId");

  const [selectedCycleId, setSelectedCycleId] = useState<number>(paramCycleId ? Number(paramCycleId) : 1);
  const [cyclesList, setCyclesList] = useState<any[]>([
    { id: 1, name: " " }
  ]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Load initiated cycles list from API or fallback to localStorage
  useEffect(() => {
    const loadCycles = async () => {
      let loaded = false;
      try {
        const response = await Api.get(API_URLS.PROMOTION.CYCLES, true);
        console.log("CYCLES API Response (Probation Confirmation):", response);
        if (response && response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          const list = response.data.data.map((c: any) => ({
            id: c.id,
            name: c.cycleName || (c.gradeTo ? `${c.gradeFrom} to ${c.gradeTo} Promotion - ${c.quarter} ${c.financialYear}` : `${c.gradeFrom || ""} Promotion - ${c.quarter} ${c.financialYear}`)
          }));
          setCyclesList(list);
          loaded = true;

          if (!paramCycleId && list.length > 0) {
            setSelectedCycleId(list[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch cycles from API, falling back to local storage", error);
      }

      if (!loaded) {
        const stored = localStorage.getItem("promotion_cycles");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const hasCycle1 = parsed.some((c: any) => c.id === 1);
              const list = hasCycle1 ? parsed : [{ id: 1, name: " " }, ...parsed];
              setCyclesList(list);
              if (list.length > 0 && !paramCycleId) {
                setSelectedCycleId(list[0].id);
              }
            }
          } catch (e) {
            console.error("Failed to parse promotion cycles from localStorage", e);
          }
        }
      }
    };
    loadCycles();
  }, []);

  // Fetch employees checklist for the selected cycle
  useEffect(() => {
    const fetchChecks = async () => {
      setLoading(true);
      try {
        const response = await Api.post(API_URLS.PROMOTION.ELIGIBLE_DPC, {
          cycleId: selectedCycleId,
          page: 1,
          limit: 100
        }, true);

        console.log("ELIGIBLE_DPC Response (for Probation Confirmation):", response);
        let dataToSet: any[] = [];
        if (response && response.data) {
          const rootData = response.data;
          if (Array.isArray(rootData)) {
            dataToSet = rootData;
          } else if (rootData.data) {
            const innerData = rootData.data;
            if (Array.isArray(innerData)) {
              dataToSet = innerData;
            } else if (innerData.data && Array.isArray(innerData.data)) {
              dataToSet = innerData.data;
            } else if (typeof innerData === 'object' && innerData !== null) {
              const merged: any[] = [];
              Object.keys(innerData).forEach(key => {
                if (Array.isArray(innerData[key])) {
                  merged.push(...innerData[key]);
                }
              });
              dataToSet = merged;
            }
          } else if (typeof rootData === 'object' && rootData !== null) {
            const merged: any[] = [];
            Object.keys(rootData).forEach(key => {
              if (Array.isArray(rootData[key])) {
                merged.push(...rootData[key]);
              }
            });
            dataToSet = merged;
          }
        }

        setEmployees(dataToSet);
      } catch (error) {
        console.error("Failed to fetch checks", error);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };
    fetchChecks();
  }, [selectedCycleId]);

  // Map candidates to ConfirmedPromotion structure and filter by approvalStatus === APPROVED
  const parsedData = useMemo<ConfirmedPromotion[]>(() => {
    if (!Array.isArray(employees)) return [];
    return employees
      .filter((e: any) => e.approvalStatus === "APPROVED")
      .map((e: any) => ({
        emp: e.empId || "",
        name: e.empName || "",
        oldDes: e.oldDesignation || "N/A",
        oldGrade: e.currentGrade || "",
        newDes: e.newDesignation || "Senior N/A",
        newGrade: e.promotionToGrade || "",
        date: e.baseDate || "",
        order: e.cycleName || "",
        sap: e.sapSyncStatus === "Success" ? "Synced" : e.sapSyncStatus === "Failed" ? "Error" : "Pending"
      }));
  }, [employees]);

  const filteredData = useMemo(() => {
    return parsedData.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.emp.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.order.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parsedData, searchTerm]);

  const stats = useMemo(() => ({
    total: parsedData.length,
    effective: parsedData.length, // assuming all are effective based on sample
    synced: parsedData.filter(r => r.sap === "Synced").length,
    pending: parsedData.filter(r => r.sap === "Pending").length,
  }), [parsedData]);

  const getValueByPath = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  /* ---------- CONFIGURATIONS ---------- */
  const dashboardSections = [
    {
      title: "Overview",
      tiles: [
        { title: "Total Promoted", icon: "ti-users", count: stats.total, link: "#", colorClass: "text-primary" },
        { title: "Effective Immediately", icon: "ti-bolt", count: stats.effective, link: "#", colorClass: "text-success" },
        { title: "SAP Synced", icon: "ti-refresh", count: stats.synced, link: "#", colorClass: "text-info" },
        { title: "Pending SAP Sync", icon: "ti-clock", count: stats.pending, link: "#", colorClass: "text-warning" },
      ]
    }
  ];

  const columns: TableColumn<ConfirmedPromotion>[] = [
    { title: "Sr. No.", key: "sr", render: (_: any, __: any, idx: number) => idx + 1 },
    { title: "Emp No", key: "emp", className: "fw-medium text-dark" },
    { title: "Name", key: "name", render: (v: string) => <div className="fw-medium">{v}</div> },
    {
      title: "Current Set", key: "current", render: (_: any, record: ConfirmedPromotion) => (
        <div>
          <div className="small text-muted">{record.oldDes}</div>
          <span className="badge bg-light text-dark border mt-1">{record.oldGrade}</span>
        </div>
      )
    },
    {
      title: "New Set", key: "new", render: (_: any, record: ConfirmedPromotion) => (
        <div>
          <div className="fw-medium text-primary">{record.newDes}</div>
          <span className="badge bg-primary-light text-primary border mt-1">{record.newGrade}</span>
        </div>
      )
    },
    { title: "Effective Date", key: "date" },
    { title: "Office Order No", key: "order", className: "text-muted" },
    {
      title: "SAP Status",
      key: "sap",
      className: "text-center",
      render: (v: string) => (
        <span className={`badge ${v === 'Synced' ? 'bg-success' : v === 'Error' ? 'bg-danger' : 'bg-warning'}`}>{v}</span>
      )
    },
    {
      title: "Actions",
      key: "actions",
      className: "text-end",
      render: (_: any, record: ConfirmedPromotion) => (
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-icon btn-sm btn-outline-primary" title="View Detail" onClick={() => alert(`Detail for ${record.name}`)}><i className="ti ti-eye"></i></button>
          <button className="btn btn-icon btn-sm btn-outline-info" title="Download Order" onClick={() => alert(`Download Order for ${record.name}`)}><i className="ti ti-download"></i></button>
        </div>
      )
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Post-Promotion View</h3>
            <p className="text-muted small mb-0">List of promoted employees and their details</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary">Export Excel</button>
            <button className="btn btn-outline-secondary">Download All Orders</button>
          </div>
        </div>

        {/* DASHBOARD SECTIONS */}
        {dashboardSections.map((section, sIdx) => (
          <DashboardSection key={sIdx} title={section.title}>
            {section.tiles.map((tile, tIdx) => (
              <DashboardTile
                key={tIdx}
                title={tile.title}
                icon={tile.icon}
                count={tile.count}
                link={tile.link}
                colorClass={tile.colorClass}
              />
            ))}
          </DashboardSection>
        ))}

        <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
          <div className="card-header bg-white py-3 border-bottom-0" style={{ borderRadius: '12px 12px 0 0' }}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
              <h5 className="card-title mb-0">Promoted Employees List</h5>
              <div className="d-flex gap-2 w-100 w-md-auto">
                <input
                  className="form-control form-control-sm"
                  placeholder="Search employees..."
                  style={{ maxWidth: 280 }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                  className="form-select form-select-sm w-auto"
                  value={selectedCycleId}
                  onChange={(e) => setSelectedCycleId(Number(e.target.value))}
                >
                  {cyclesList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="card-body p-0" style={{ position: "relative" }}>
            {loading && (
              <div className="d-flex align-items-center justify-content-center bg-white opacity-75 position-absolute w-100 h-100" style={{ zIndex: 10, minHeight: 200 }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover table-center mb-0">
                <thead className="thead-light">
                  <tr>
                    {columns.map((col, idx) => (
                      <th key={idx} className={col.className || ""}>{col.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((record, idx) => (
                    <tr key={idx}>
                      {columns.map((col, cIdx) => (
                        <td key={cIdx} className={col.className || ""}>
                          {col.render ? col.render(getValueByPath(record, col.key), record, idx) : getValueByPath(record, col.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {filteredData.length === 0 && !loading && (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-4 text-muted">
                        No approved promoted employees found for this cycle.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <style>{`
        .btn-icon {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 4px;
        }
        .bg-primary-light { background-color: rgba(var(--bs-primary-rgb), 0.08); }
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
      `}</style>
      </div>
    </div>
  );
};

export default ProbationConfirmation;
